import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Mutation } from '@apollo/client/react/components';
import { TypedDocumentNode, FetchResult } from '@apollo/client';

import { AlertContext } from 'lib/alert-context/alert-context';

import ActionSheet, { Action } from 'four-nets/ui-v2/action-sheet/action-sheet';
import ComplainDialog from 'four-nets/ui-v2/complain-dialog/complain-dialog';
import { ViewerContext } from 'four-nets/user/viewer/viewer';
import complainCommentMutation, {
    ComplainCommentMutation,
    ComplainCommentMutationVariables,
} from 'four-nets/photoblog/posts/common/comments/comment/mutations/complain-comment.graphql';

import query, {
    CommentsQuery,
    CommentsQueryVariables,
} from 'four-nets/photoblog/posts/common/comments/comments.graphql';

import { CommentFooterFragment } from '../footer.graphql';

import removeMutation, {
    RemoveCommentMutation,
    RemoveCommentMutationVariables,
} from './mutations/remove-comment.graphql';

export interface Props {
    id: string;
    open: boolean;
    onClose: () => void;

    post: CommentFooterFragment['post'];
}

class CommentMore extends React.PureComponent<Props> {
    static contextType = ViewerContext;

    render() {
        const { id, onClose, open, post } = this.props;

        const isAuth = this.context?.viewer?.username;
        const isPostAuthor = Boolean(isAuth && post?.user?.username === this.context.viewer.username);

        return (
            <ComplainDialog>
                {({ complain: openComplain }) => (
                    <ActionSheet open={open} onClose={onClose}>
                        {({ close }) => (
                            <>
                                <Mutation<ComplainCommentMutation, ComplainCommentMutationVariables>
                                    mutation={complainCommentMutation}
                                >
                                    {complain => (
                                        <Action
                                            onClick={() => {
                                                if (onClose) {
                                                    onClose();
                                                }

                                                openComplain({
                                                    title: <FormattedMessage id='COMPLAIN_IMAGE' />,
                                                    onSubmit: ({ comment }) => complain({ variables: { id, comment } }),
                                                });
                                            }}
                                        >
                                            <FormattedMessage id='REPORT' />
                                        </Action>
                                    )}
                                </Mutation>
                                {isPostAuthor ? (
                                    <Mutation<RemoveCommentMutation, RemoveCommentMutationVariables>
                                        mutation={removeMutation}
                                        variables={{ input: { id } }}
                                        update={(cache, { data: result }) => {
                                            if (result?.removeComment?.post) {
                                                const variables = { parentId: result.removeComment.post.id };
                                                const cached = cache.readQuery<CommentsQuery, CommentsQueryVariables>({
                                                    query,
                                                    variables,
                                                });

                                                if (cached) {
                                                    cache.writeQuery({
                                                        query,
                                                        variables,
                                                        data: {
                                                            ...cached,
                                                            comments: {
                                                                ...cached.comments,
                                                                edges: [
                                                                    ...cached.comments!.edges.filter(
                                                                        edge => edge?.node?.id !== id
                                                                    ),
                                                                ],
                                                            },
                                                        },
                                                    });
                                                }
                                            }
                                        }}
                                    >
                                        {remove => (
                                            <AlertContext.Consumer>
                                                {({ confirm }) => {
                                                    return (
                                                        <Action
                                                            onClick={() => {
                                                                if (onClose) {
                                                                    onClose();
                                                                }

                                                                confirm({
                                                                    acceptButtonLabel: (
                                                                        <FormattedMessage
                                                                            id='DELETE_COMMENT'
                                                                            values={{ gender: 'v' }}
                                                                        />
                                                                    ),
                                                                    body: (
                                                                        <FormattedMessage
                                                                            id='ARE_YOU_SURE_YOU_WANT_TO_DELETE_COMMENT'
                                                                            values={{ gender: 'v' }}
                                                                        />
                                                                    ),
                                                                    onAccept: () => {
                                                                        remove();
                                                                    },
                                                                    acceptButtonType: 'warn',
                                                                });
                                                            }}
                                                        >
                                                            <FormattedMessage
                                                                id='DELETE_COMMENT'
                                                                values={{ gender: 'v' }}
                                                            />
                                                        </Action>
                                                    );
                                                }}
                                            </AlertContext.Consumer>
                                        )}
                                    </Mutation>
                                ) : null}
                                {close}
                            </>
                        )}
                    </ActionSheet>
                )}
            </ComplainDialog>
        );
    }
}

export default CommentMore;
